/**
 * 1-common.js
 * Common utilities and fixes for custom theme functionality.
 */

/**
 * Anchor Scroll Fix
 * Single offset: header height + gutter.
 * Click + page-load use manual calculation.
 * scroll-padding-top kept in sync for native browser anchor jumps
 * (e.g. pressing Enter on same URL in address bar).
 * Hash stays in URL.
 */
(function () {
  var SCROLL_GUTTER = 20;
  var SCROLL_DURATION = 1;
  var DEBUG = true;

  function log() {
    if (!DEBUG) return;
    var args = ["%c[AnchorScroll]", "color:#0af;font-weight:bold"];
    for (var i = 0; i < arguments.length; i++) args.push(arguments[i]);
    console.log.apply(console, args);
  }

  function getHeaderOffset() {
    var headerComp = document.querySelector("#header-component[sticky]");
    var headerEl = document.querySelector("#header-component");
    var compH = headerComp ? headerComp.offsetHeight : 0;
    var elH = headerEl ? headerEl.offsetHeight : 0;
    return compH || elH || 0;
  }

  // Keep scroll-padding-top in sync with header height so native browser
  // anchor jumps (address-bar Enter, back/forward) respect the sticky header.
  function updateScrollPadding() {
    var offset = getHeaderOffset() + SCROLL_GUTTER;
    document.documentElement.style.scrollPaddingTop = offset + "px";
  }
  updateScrollPadding();
  window.addEventListener("resize", updateScrollPadding);
  var _rafId = null;
  window.addEventListener("scroll", function () {
    if (!_rafId) {
      _rafId = requestAnimationFrame(function () {
        updateScrollPadding();
        _rafId = null;
      });
    }
  }, { passive: true });

  function findTarget(id) {
    return (
      document.getElementById(id) ||
      document.querySelector('[data-anchor="' + id + '"]') ||
      document.querySelector('[id*="' + id + '"]')
    );
  }

  function getScrollY(target) {
    var rect = target.getBoundingClientRect();
    var scrollY = window.scrollY || window.pageYOffset;
    var offset = getHeaderOffset() + SCROLL_GUTTER;
    return Math.max(0, rect.top + scrollY - offset);
  }

  /** Eased smooth scroll — no dependencies */
  function animateScroll(from, to, duration, done) {
    var start = performance.now();
    function ease(t) {
      return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    }
    function step(now) {
      var elapsed = now - start;
      var progress = Math.min(elapsed / duration, 1);
      window.scrollTo(0, from + (to - from) * ease(progress));
      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        if (done) done();
      }
    }
    requestAnimationFrame(step);
  }

  function scrollToTarget(target, done) {
    var y = getScrollY(target);
    var from = window.scrollY || window.pageYOffset;
    document.documentElement.style.scrollBehavior = "auto";
    animateScroll(from, y, SCROLL_DURATION * 1000, function () {
      // Correction pass: header height may differ at new scroll position
      // (e.g. sticky header shrinks, announcement bar collapses).
      // Recalculate with the header as it appears now and snap if needed.
      var correctedY = getScrollY(target);
      if (Math.abs(correctedY - y) > 2) {
        window.scrollTo(0, correctedY);
      }
      document.documentElement.style.scrollBehavior = "";
      if (window.ScrollTrigger) window.ScrollTrigger.refresh();
      if (done) done();
    });
  }

  // --- Click handler (smooth animated scroll) ---
  document.addEventListener("click", function (e) {
    var link = e.target.closest('a[href*="#"]');
    if (!link) return;

    var url = new URL(link.href, window.location.origin);
    if (url.pathname !== window.location.pathname && url.pathname !== "") return;

    var hash = url.hash;
    if (!hash || hash === "#") return;

    var targetId = hash.substring(1);
    var target = findTarget(targetId);
    if (!target) return;

    e.preventDefault();

    // Skip-to-content links must move keyboard focus into the target, not just
    // scroll — moving focus is the whole point of the link. Without this the
    // generic handler below would compute a ~0 offset (the target sits just
    // below the header) and appear to do nothing. (Allyant a11y #86)
    if (link.classList.contains("skip-to-content-link")) {
      if (!target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1");
      target.focus({ preventScroll: true });
    }

    scrollToTarget(target);
  });

  // --- Page-load handler (direct visit with hash) ---
  // Waits for page height to stabilize (3rd-party apps inject content),
  // then scrolls to hash target with the same offset as clicks.
  //
  // Strip the hash immediately via replaceState so the browser cannot
  // perform its native anchor jump (which ignores sticky-header offset).
  // The hash is restored after our custom scroll finishes.
  var __savedHash = window.location.hash ? window.location.hash.substring(1) : "";
  var __cleanUrl = window.location.href.split("#")[0];

  if (__savedHash) {
    // Remove hash to block native jump
    history.replaceState(null, "", __cleanUrl);
    // Force scroll to top so the native jump has no residual effect
    if ("scrollRestoration" in history) history.scrollRestoration = "manual";
    window.scrollTo(0, 0);

    var STABLE_MS = 800;
    var MAX_WAIT_MS = 5000;

    function waitForStableHeight(callback) {
      var lastHeight = document.documentElement.scrollHeight;
      var stableTimer = null;
      var startTime = Date.now();

      function check() {
        var currentHeight = document.documentElement.scrollHeight;
        if (currentHeight !== lastHeight) {
          lastHeight = currentHeight;
          clearTimeout(stableTimer);
          stableTimer = null;
        }
        if (Date.now() - startTime >= MAX_WAIT_MS) {
          clearTimeout(stableTimer);
          callback();
          return;
        }
        if (!stableTimer) {
          stableTimer = setTimeout(callback, STABLE_MS);
        }
        requestAnimationFrame(check);
      }
      requestAnimationFrame(check);
    }

    function scrollOnLoad() {
      waitForStableHeight(function () {
        var target = findTarget(__savedHash);
        if (!target) return;
        scrollToTarget(target, function () {
          // Restore hash without triggering native jump
          history.replaceState(null, "", __cleanUrl + "#" + __savedHash);
        });
      });
    }

    if (document.readyState === "complete") {
      scrollOnLoad();
    } else {
      window.addEventListener("load", scrollOnLoad);
    }
  }

  // --- Hashchange handler (address-bar re-submit / back-forward) ---
  // Catches the case where a user presses Enter on the same URL or
  // navigates to a different hash without a full page reload.
  window.addEventListener("hashchange", function () {
    var hash = window.location.hash ? window.location.hash.substring(1) : "";
    if (!hash) return;
    var target = findTarget(hash);
    if (!target) return;
    // Prevent the native jump that already happened
    requestAnimationFrame(function () {
      scrollToTarget(target);
    });
  });

  // .c-button hover effect: write cursor position to inner <span> so CSS can
  // anchor a ripple/reveal at the entry/exit point. Delegated so it covers
  // buttons rendered after load (cart drawer, quick-add, section morphs).
  function updateButtonOrigin(btn, e) {
    var targets = btn.querySelectorAll("span.anim-child");
    if (!targets.length) return;
    var rect = btn.getBoundingClientRect();
    var top = e.clientY - rect.top + "px";
    var left = e.clientX - rect.left + "px";
    targets.forEach(function (el) {
      el.style.top = top;
      el.style.left = left;
    });
  }

  document.addEventListener("mouseover", function (e) {
    var target = e.target;
    if (!(target instanceof Element)) return;
    var btn = target.closest(".c-button");
    if (btn) updateButtonOrigin(btn, e);
  });

  document.addEventListener("mouseout", function (e) {
    var target = e.target;
    if (!(target instanceof Element)) return;
    var btn = target.closest(".c-button");
    if (btn) updateButtonOrigin(btn, e);
  });
  // Klaviyo form: inject c-button class + anim-child span into the subscribe
  // button so the theme's ripple animation works. Uses MutationObserver since
  // Klaviyo renders its form asynchronously after page load.
  // Observer stays connected because Klaviyo re-renders the button on
  // form error/success, which strips the added classes.
  function patchKlaviyoButton(container) {
    var btn = container.querySelector('.klaviyo-form-button, button[type="button"]');
    if (!btn) return false;
    if (!btn.classList.contains('c-button')) {
      btn.classList.add('button');
      btn.classList.add('c-button');
    }
    if (!btn.querySelector('span.anim-child')) {
      var span = document.createElement('span');
      span.className = 'anim-child';
      btn.insertBefore(span, btn.firstChild);
    }

    return true;
  }

  // Toggle .klaviyo-integrated-row on desktop, remove on mobile.
  var DESKTOP_BP = 750;
  var integratedRows = [];

  function updateIntegratedRows() {
    var isDesktop = window.innerWidth >= DESKTOP_BP;
    integratedRows.forEach(function (row) {
      var hasEmailInput = !!row.querySelector('input[type="email"]');
      if (isDesktop && hasEmailInput) {
        if (!row.classList.contains('klaviyo-integrated-row')) {
          row.classList.add('klaviyo-integrated-row');
        }
      } else {
        row.classList.remove('klaviyo-integrated-row');
      }
    });
  }

  function patchIntegrated(container) {
    var integrated = container.closest('.email-integrated');
    if (!integrated) return;
    var emailRow = container.querySelector('[data-testid="form-row"]');
    if (!emailRow) return;
    if (integratedRows.indexOf(emailRow) === -1) {
      integratedRows.push(emailRow);
    }
    updateIntegratedRows();
  }

  window.addEventListener('resize', updateIntegratedRows);

  // a11y (#91): Klaviyo renders its own validation error text into the last
  // child of the .klaviyo-emailinput wrapper and toggles aria-invalid on the
  // input, but does not associate the two or move focus. Wire aria-describedby
  // onto the email field and focus it once when an error appears.
  function patchKlaviyoEmailErrors(container) {
    var input = container.querySelector('input[type="email"]');
    if (!input) return;
    var wrapper = input.closest('.klaviyo-emailinput') || input.parentElement;
    if (!wrapper) return;

    var errorContainer = wrapper.querySelector(':scope > div:last-child');
    var hasError =
      input.getAttribute('aria-invalid') === 'true' &&
      errorContainer &&
      errorContainer.textContent.trim().length > 0;

    if (hasError) {
      if (!errorContainer.id) {
        errorContainer.id = (input.id || 'klaviyo-email') + '-error';
      }
      var ids = (input.getAttribute('aria-describedby') || '').split(' ').filter(Boolean);
      if (ids.indexOf(errorContainer.id) === -1) {
        ids.push(errorContainer.id);
        input.setAttribute('aria-describedby', ids.join(' '));
      }
      // Move focus to the errored field once per error cycle.
      if (input.dataset.klErrorFocused !== 'true' && document.activeElement !== input) {
        input.dataset.klErrorFocused = 'true';
        try { input.focus(); } catch (e) {}
      }
    } else {
      if (errorContainer && errorContainer.id) {
        var kept = (input.getAttribute('aria-describedby') || '').split(' ').filter(function (id) {
          return id && id !== errorContainer.id;
        });
        if (kept.length) input.setAttribute('aria-describedby', kept.join(' '));
        else input.removeAttribute('aria-describedby');
      }
      input.dataset.klErrorFocused = '';
    }
  }

  // a11y (#15): the required-field asterisk must exist in the page markup, not
  // as CSS generated content. Append a real .c-form-required-marker span to the
  // email label of Klaviyo-rendered forms ("Email *"). aria-hidden because the
  // input's own required/aria-required already announces the state to AT; the
  // glyph plus the "* indicates a required field" legend (rendered by the
  // 1-klaviyo-embed block) carry the visual indication.
  function patchKlaviyoRequiredMarker(container) {
    var label = container.querySelector('.klaviyo-emailinput label');
    if (!label || label.querySelector('.c-form-required-marker')) return;
    var marker = document.createElement('span');
    marker.className = 'c-form-required-marker';
    marker.setAttribute('aria-hidden', 'true');
    marker.textContent = '*';
    label.appendChild(marker);
  }

  function initKlaviyoPatch() {
    var containers = document.querySelectorAll('[class*="klaviyo-form-"]');
    containers.forEach(function (container) {
      // Patch if already rendered
      patchKlaviyoButton(container);
      patchIntegrated(container);
      patchKlaviyoEmailErrors(container);
      patchKlaviyoRequiredMarker(container);
      // Keep watching — Klaviyo re-renders on error/success which strips classes
      var observer = new MutationObserver(function () {
        patchKlaviyoButton(container);
        patchIntegrated(container);
        patchKlaviyoEmailErrors(container);
        patchKlaviyoRequiredMarker(container);
      });
      observer.observe(container, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['aria-invalid']
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initKlaviyoPatch);
  } else {
    initKlaviyoPatch();
  }
})();
